// Project status helpers
// Centralizes status normalization + colors so cards / pages stay consistent.

export const PROJECT_STATUS_DEFS = {
  "in-development": {
    key: "in-development",
    label: "In Development",
    color: "#1E88E5",
    icon: "🛠️",
  },
  released: {
    key: "released",
    label: "Released",
    color: "#4CAF50",
    icon: "✅",
  },
  prototype: {
    key: "prototype",
    label: "Prototype",
    color: "#FB8C00",
    icon: "🧪",
  },
  canceled: {
    key: "canceled",
    label: "Canceled",
    color: "#E53935",
    icon: "🛑",
  },
  archived: {
    key: "archived",
    label: "Archived",
    color: "#9E9E9E",
    icon: "📦",
  },
  "early-access": {
    key: "early-access",
    label: "Early Access",
    color: "#8E24AA",
    icon: "🚧",
  },

  // NEW: Game Jam
  gamejam: {
    key: "gamejam",
    label: "Game Jam",
    color: "#8E24AA", // best pick: purple feels "event/award" and doesn't clash with others
    icon: "🏆",
  },
};

const STATUS_SYNONYMS = {
  // Common variants
  published: "released",
  publish: "released",
  shipped: "released",
  live: "released",
  release: "released",
  released: "released",

  "in development": "in-development",
  indevelopment: "in-development",
  "in-development": "in-development",
  development: "in-development",
  dev: "in-development",

  prototyping: "prototype",
  prototype: "prototype",
  poc: "prototype",

  cancelled: "canceled",
  canceled: "canceled",
  cancelled_project: "canceled",

  archived: "archived",
  archive: "archived",

  "early access": "early-access",
  "early-access": "early-access",
  ea: "early-access",

  // NEW: explicit jam synonyms (exact-ish tags)
  jam: "gamejam",
  gamejam: "gamejam",
  "game-jam": "gamejam",
  "game jam": "gamejam",
  ggj: "gamejam",
};

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[_\s]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .trim();
}

function normalizeStatusKey(input) {
  if (!input) return null;
  const raw = norm(input);
  if (!raw) return null;

  // Exact match
  if (PROJECT_STATUS_DEFS[raw]) return raw;
  if (STATUS_SYNONYMS[raw]) return STATUS_SYNONYMS[raw];

  // Handle tag-like strings: "In-Development", "In Development", etc.
  const softened = raw.replace(/-+/g, "-");
  if (PROJECT_STATUS_DEFS[softened]) return softened;
  if (STATUS_SYNONYMS[softened]) return STATUS_SYNONYMS[softened];

  return null;
}

// NEW: detect jam-ish tags even when they are not exact matches,
// e.g. "Global Game Jam 2024", "Mini Jam #43", "GameJamPlus 24/25"
function isJamLikeTag(tag) {
  const t = String(tag || "").toLowerCase();

  // Keep this intentionally broad to catch your tags:
  // "Mini Jame Gam #43" (typo), "Global Game Jam 2024", "GameJamPlus 24/25", etc.
  return (
    t.includes("gamejam") ||
    t.includes("game jam") ||
    t.includes("game-jam") ||
    t.includes("ggj") ||
    t.includes("global game jam") ||
    t.includes("mini jam") ||
    t.includes("gamejamplus") ||
    // broad "jam" catch, but avoid false positives like "james"
    /\bjam\b/i.test(t)
  );
}

function findStatusKeyFromTags(tags) {
  if (!Array.isArray(tags)) return null;

  // 1) Prefer real statuses first (so jam doesn't override Released/In-Dev/etc.)
  for (const t of tags) {
    const key = normalizeStatusKey(t);
    if (key && key !== "gamejam") return key;
  }

  // 2) If no real status found, allow jam inference
  for (const t of tags) {
    const key = normalizeStatusKey(t);
    if (key === "gamejam") return "gamejam";
  }

  // 3) Soft match: any jam-like tag becomes gamejam
  for (const t of tags) {
    if (isJamLikeTag(t)) return "gamejam";
  }

  return null;
}

/**
 * Returns a normalized status meta object (key/label/color/icon) for a project.
 * Works with either `project.status` or a matching string inside `project.techTags`.
 */
export function getProjectStatusMeta(project) {
  if (!project) return null;

  const fromField = normalizeStatusKey(project.status);
  // If status field explicitly says gamejam, allow it — but otherwise keep field priority.
  if (fromField) return PROJECT_STATUS_DEFS[fromField] || null;

  const fromTags = findStatusKeyFromTags(project.techTags);
  if (!fromTags) return null;

  return PROJECT_STATUS_DEFS[fromTags] || null;
}

/**
 * Removes status-like tags from a tag list, so you can render them separately as a badge.
 * Also strips jam-like tags (Game Jam / GGJ / Mini Jam etc.) so it doesn't duplicate the badge.
 */
export function stripStatusTags(tags) {
  if (!Array.isArray(tags)) return [];

  return tags.filter((t) => {
    // strip real status tags
    if (normalizeStatusKey(t)) return false;
    // strip jam-ish tags too
    if (isJamLikeTag(t)) return false;
    return true;
  });
}
